import { FC, MouseEvent, useEffect, useId, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Tooltip } from 'primereact/tooltip';
import { Presentation } from 'lucide-react';
import { useRelayEnvironment } from 'react-relay';
import { fetchQuery } from 'relay-runtime';
import { getLessonsByType } from '../../graphQLOperations/Skills';
import { SkillsLessonByTypeQuery } from '../../graphQLOperations/__generated__/SkillsLessonByTypeQuery.graphql';
import {
  pickTeacherLedFiles,
  TEACHER_LED_TYPE_ID,
  TeacherLedFiles,
} from '../../businessLogicService/pickTeacherLedFiles';
import SlideViewer from './SlideViewer';
import { preloadPptxModule } from './viewers';
import LessonCoachmark from './LessonCoachmark';

const COACHMARK_STORAGE_KEY = 'tlr-coachmark-dismissed';

interface Props {
  skill: { id: string; code: string; name: string };
  disabled?: boolean;
  showCoachmark?: boolean;
}

export const PresentLessonButton: FC<Props> = ({ skill, disabled, showCoachmark }) => {
  const environment = useRelayEnvironment();
  const toast = useRef<Toast>(null);
  const anchorRef = useRef<HTMLSpanElement>(null);
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState<TeacherLedFiles | null>(null);
  const tooltipClass = `nc-tlr-present-${useId().replace(/:/g, '')}`;
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(COACHMARK_STORAGE_KEY) === '1');
  const coachmarkVisible = !!showCoachmark && !dismissed;

  useEffect(() => {
    if (!disabled) preloadPptxModule();
  }, [disabled]);

  const dismiss = () => {
    localStorage.setItem(COACHMARK_STORAGE_KEY, '1');
    setDismissed(true);
  };

  const openLesson = async (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setLoading(true);
    try {
      const data = await fetchQuery<SkillsLessonByTypeQuery>(environment, getLessonsByType, {
        filter: { skillId: skill.id, lessonTypeIds: [TEACHER_LED_TYPE_ID], isActive: true },
      }).toPromise();
      const picked = pickTeacherLedFiles(data);
      if (picked?.slides) {
        setFiles(picked);
      } else {
        toast.current?.show({ severity: 'info', summary: 'No Teacher-Led lesson for this skill yet.' });
      }
    } catch {
      toast.current?.show({ severity: 'error', summary: 'Could not load the Teacher-Led lesson. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Tooltip
        target={`.${tooltipClass}`}
        position="top"
        className="tw-font-atkinson !tw-rounded-lg"
        disabled={coachmarkVisible}
      >
        {disabled ? 'No Teacher-Led lesson for this skill yet.' : 'Present Teacher-Led Lesson'}
      </Tooltip>
      <span ref={anchorRef} className={`${tooltipClass} tw-inline-flex`} onClick={e => e.stopPropagation()}>
        <Button
          text
          rounded
          loading={loading}
          disabled={disabled}
          aria-label="`Teacher led-resources"
          className={`!tw-h-10 !tw-w-10 !tw-p-0 !tw-border-none !tw-bg-transparent ${
            disabled
              ? '!tw-text-neutral-600 !tw-opacity-100'
              : '!tw-text-indigo-600 hover:!tw-bg-neutral-100 focus:!tw-bg-indigo-600 focus:!tw-text-white'
          }`}
          icon={<Presentation size={18} />}
          onClick={openLesson}
        />
      </span>
      {coachmarkVisible && <LessonCoachmark anchorRef={anchorRef} onDismiss={dismiss} />}
      <Toast ref={toast} position="top-right" appendTo={document.body} />
      {files?.slides && (
        <SlideViewer
          url={files.slides.url}
          guide={files.guide}
          skillCode={skill.code}
          title={skill.name || files.slides.name}
          onClose={() => setFiles(null)}
        />
      )}
    </>
  );
};
